import React, { useState, useEffect, useRef } from 'react';
import { Key, Plus, Code, BookOpen, FileJson } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiService from '../api/api';
import DeleteConfirmationModal from './DeleteConfirmationModal';

function APIAccess({ user }) {
  const navigate = useNavigate();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showCopyNotification, setShowCopyNotification] = useState({});
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, keyId: null, keyName: null, isDeleting: false });
  const keysFetchRef = useRef(false);

  // Fetch API keys on mount
  useEffect(() => {
    if (keysFetchRef.current) return;
    keysFetchRef.current = true;
    fetchAPIKeys();
  }, []);

  const fetchAPIKeys = async () => {
    try {
      setLoading(true);
      const data = await apiService.getUserAPIKeys();
      setApiKeys(Array.isArray(data) ? data : data.keys || []);
    } catch (err) {
      console.error('Error fetching API keys:', err);
      setError('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    if (!newKeyName.trim()) {
      setError('Please enter a name for your API key');
      return;
    }

    // Check if user already has 3 API keys
    if (apiKeys.length >= 3) {
      setError('Maximum API key limit reached. You can have up to 3 API keys. Please delete an existing key to create a new one.');
      return;
    }

    try {
      setCreating(true);
      setError(null);
      setSuccess(null);

      const response = await apiService.createAPIKey({ name: newKeyName.trim() });

      // Store the created key for display
      setCreatedKey({
        key: response.key,
        keyData: response.key_data,
        message: response.message,
      });

      // Reset form
      setNewKeyName('');
      setSuccess('API key created successfully!');

      // Refresh the keys list
      await fetchAPIKeys();
    } catch (err) {
      setError(err.detail || err.message || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteKey = (keyId, keyName) => {
    setDeleteModal({
      isOpen: true,
      keyId,
      keyName,
      isDeleting: false,
    });
  };

  const handleConfirmDelete = async () => {
    const { keyId } = deleteModal;
    setDeleteModal((prev) => ({ ...prev, isDeleting: true }));

    try {
      setError(null);
      await apiService.deleteAPIKey(keyId);
      setSuccess('API key deleted successfully');
      setDeleteModal({ isOpen: false, keyId: null, keyName: null, isDeleting: false });
      await fetchAPIKeys();
    } catch (err) {
      setError('Failed to delete API key');
      setDeleteModal({ isOpen: false, keyId: null, keyName: null, isDeleting: false });
    }
  };

  const handleCancelDelete = () => {
    setDeleteModal({ isOpen: false, keyId: null, keyName: null, isDeleting: false });
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setShowCopyNotification({ [label]: true });
      setTimeout(() => {
        setShowCopyNotification({});
      }, 2000);
    }).catch(() => {
      setError('Failed to copy to clipboard');
    });
  };

  if (!user) {
    return (
      <div className="auth-required">
        <p>Please sign in to access API settings</p>
      </div>
    );
  }

  return (
    <div className="content-wrapper">
      <div className="content-inner">
        <div className="card-container">
          {/* Header */}
          <div style={{ marginBottom: '32px' }}>
            <h2 className="section-title mb-2">
              <Key size={20} strokeWidth={2} style={{ marginRight: '8px', display: 'inline-block' }} />
              API Access
            </h2>
            <p className="subtitle">Manage your API keys for programmatic access</p>
          </div>

          {/* Error and Success Messages */}
          {error && (
            <div className="error-alert" style={{ marginBottom: '16px' }}>
              <p>{error}</p>
            </div>
          )}
          {success && (
            <div className="alert-success" style={{ marginBottom: '16px' }}>
              <p>{success}</p>
            </div>
          )}

          {/* Create New Key Section */}
          <div style={{ marginBottom: '32px', paddingBottom: '32px', borderBottom: '1px solid var(--border-color)' }}>
            <h3 className="section-title mb-2" style={{ fontSize: '16px' }}>
              <Plus size={18} strokeWidth={2} style={{ marginRight: '8px', display: 'inline-block' }} />
              Create New API Key
            </h3>
            <p className="helper-text" style={{ marginBottom: '12px', fontSize: '13px' }}>
              You can create up to 3 API keys. Currently using {apiKeys.length} of 3.
            </p>
            <form onSubmit={handleCreateKey} className="create-key-form" style={{ gap: '12px', marginTop: '16px' }}>
              <input
                type="text"
                placeholder="Enter a name for this API key"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                disabled={creating || apiKeys.length >= 3}
                className="create-key-input"
                style={{
                  padding: '10px 16px',
                  background: 'rgba(22, 23, 29, 0.6)',
                  border: '1.5px solid var(--border-color)',
                  borderRadius: '8px',
                  color: 'var(--text-primary)',
                  fontSize: '14px',
                  opacity: apiKeys.length >= 3 ? 0.5 : 1,
                }}
              />
              <button
                type="submit"
                disabled={creating || apiKeys.length >= 3}
                className="btn-submit create-key-button"
                style={{
                  opacity: apiKeys.length >= 3 ? 0.5 : 1,
                  cursor: apiKeys.length >= 3 ? 'not-allowed' : 'pointer',
                }}
              >
                {creating ? 'Creating...' : apiKeys.length >= 3 ? 'Limit Reached' : 'Create API Key'}
              </button>
            </form>
          </div>

          {/* Display Created Key */}
          {createdKey && (
            <div className="api-key-warning" style={{ marginBottom: '32px' }}>
              <p style={{ marginBottom: '12px', fontWeight: '600' }}>🔐 New API Key Created</p>
              <p style={{ marginBottom: '16px' }}>{createdKey.message}</p>
              <div className="api-key-display">
                <span style={{ wordBreak: 'break-all' }}>{createdKey.key}</span>
              </div>
              <button
                className="btn-copy"
                onClick={() => copyToClipboard(createdKey.key, 'key')}
                style={{ marginTop: '12px' }}
              >
                {showCopyNotification.key ? '✓ Copied!' : 'Copy Key'}
              </button>
            </div>
          )}

          {/* API Keys List */}
          <div className="api-keys-section" style={{ marginBottom: '32px', paddingBottom: '32px', borderBottom: '1px solid var(--border-color)' }}>
            <h3 className="section-title mb-2" style={{ fontSize: '16px' }}>
              <Key size={18} strokeWidth={2} style={{ marginRight: '8px', display: 'inline-block' }} />
              Your API Keys
            </h3>
            {loading ? (
              <p className="helper-text">Loading API keys...</p>
            ) : apiKeys.length === 0 ? (
              <p className="helper-text">No API keys created yet</p>
            ) : (
              <div className="api-keys-grid">
                {apiKeys.map((key) => (
                  <div key={key.id} className="api-key-card">
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: 'var(--text-primary)' }}>
                        {key.name}
                      </p>
                      <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                        {key.key_prefix}
                      </p>
                      <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                        Created: {new Date(key.created_at).toLocaleDateString()}
                      </p>
                      <p style={{ margin: '0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                        Last used: {key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : 'Never'}
                      </p>
                    </div>
                    <div className="api-key-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexDirection: 'row' }}>
                      <span
                        style={{
                          fontSize: '11px',
                          background: key.is_active ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                          color: key.is_active ? 'var(--success-color)' : 'var(--error-color)',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {key.is_active ? 'Active' : 'Revoked'}
                      </span>
                      <button
                        onClick={() => handleDeleteKey(key.id, key.name)}
                        style={{
                          background: 'rgba(239, 68, 68, 0.1)',
                          border: '1px solid rgba(239, 68, 68, 0.3)',
                          color: 'var(--error-color)',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* API Documentation Link */}
          <div style={{ marginBottom: '32px' }}>
            <h3 className="section-title mb-2" style={{ fontSize: '16px' }}>
              <BookOpen size={18} strokeWidth={2} style={{ marginRight: '8px', display: 'inline-block' }} />
              API Documentation
            </h3>
            <p className="helper-text" style={{ marginBottom: '16px' }}>
              View comprehensive API documentation with code examples in cURL, Node.js, and Python.
            </p>
            <button
              className="btn-submit"
              onClick={() => navigate('/docs')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px'
              }}
            >
              <BookOpen size={18} />
              View Full API Documentation
            </button>
          </div>

        </div>

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={deleteModal.isOpen}
          title="Delete API Key"
          message={`Are you sure you want to delete the API key "${deleteModal.keyName}"? Any applications using this key will stop working.`}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          isLoading={deleteModal.isDeleting}
        />
      </div>
    </div>
  );
}

export default APIAccess;
